import * as React from "react"
import { cn } from "@/lib/utils"

export interface ConsentBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Callback invoked when user accepts consent
   * Receives the consent value (true when accepted)
   */
  onAccept: (consent: boolean) => void
  /**
   * URL for Terms & Conditions
   * @default "https://anyw3ar.com/terms"
   */
  termsUrl?: string
  /**
   * URL for Privacy Policy
   * @default "https://anyw3ar.com/privacy"
   */
  privacyUrl?: string
  /**
   * URL for Cookie Policy
   * @default "https://anyw3ar.com/cookies"
   */
  cookiesUrl?: string
  /**
   * Initial consent state
   * @default false
   */
  defaultChecked?: boolean
  /**
   * Controlled consent state
   */
  checked?: boolean
  /**
   * Custom button text
   * @default "I accept"
   */
  buttonText?: string
  /**
   * Whether to show the accept button
   * When false, onAccept is called immediately when checkbox changes
   * @default true
   */
  showButton?: boolean
}

/**
 * ConsentBanner Component
 *
 * Displays a user consent banner for Terms & Conditions, Privacy Policy, and Cookies.
 * Checkbox is unchecked by default and must be checked to enable the accept button.
 *
 * @example
 * ```tsx
 * <ConsentBanner
 *   onAccept={(consent) => console.log('Consent:', consent)}
 *   termsUrl="https://example.com/terms"
 *   privacyUrl="https://example.com/privacy"
 *   cookiesUrl="https://example.com/cookies"
 * />
 * ```
 */
export const ConsentBanner = React.forwardRef<HTMLDivElement, ConsentBannerProps>(
  (
    {
      className,
      onAccept,
      termsUrl = "https://anyw3ar.com/terms",
      privacyUrl = "https://anyw3ar.com/privacy",
      cookiesUrl = "https://anyw3ar.com/cookies",
      defaultChecked = false,
      checked,
      buttonText = "I accept",
      showButton = true,
      ...props
    },
    ref
  ) => {
    // Internal state for uncontrolled mode
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked)

    // Use controlled state if provided, otherwise use internal state
    const isChecked = checked !== undefined ? checked : internalChecked

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = event.target.checked
      if (checked === undefined) {
        setInternalChecked(newChecked)
      }

      // When showButton is false, call onAccept immediately on checkbox change
      if (!showButton) {
        onAccept(newChecked)
      }
    }

    const handleAccept = () => {
      if (isChecked) {
        onAccept(true)
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          "bg-brand-red box-border flex flex-col gap-4 items-center justify-center p-4 rounded-[0.3125rem]",
          className
        )}
        {...props}
      >
        {/* Checkbox and text container */}
        <div className="flex gap-2 items-start w-full">
          {/* Custom checkbox */}
          <label className="relative flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              className="peer sr-only"
              aria-label="Accept terms and conditions"
            />
            {/* Checkbox visual */}
            <div className="w-4 h-4 border-[0.2px] border-white rounded-[0.125rem] bg-transparent peer-checked:bg-white peer-checked:border-white transition-colors flex items-center justify-center">
              {isChecked && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-brand-red"
                >
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </label>

          {/* Consent text with links */}
          <p className="flex-1 font-sf-pro text-[0.9375rem] text-white leading-normal">
            By checking this box, you accept our{" "}
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-sf-pro font-semibold text-black underline decoration-solid hover:no-underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-red transition-all"
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-sf-pro font-semibold text-black underline decoration-solid hover:no-underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-red transition-all"
            >
              Privacy Policy
            </a>
            , and allow the use of{" "}
            <a
              href={cookiesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-sf-pro font-semibold text-black underline decoration-solid hover:no-underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-red transition-all"
            >
              cookies
            </a>{" "}
            to improve your experience.
          </p>
        </div>

        {/* Accept button - only shown when showButton is true */}
        {showButton && (
          <button
            type="button"
            onClick={handleAccept}
            disabled={!isChecked}
            className={cn(
              "bg-white box-border flex items-center justify-center gap-4 px-4 py-2 rounded-[0.375rem] w-full transition-all duration-200",
              "font-montserrat font-bold text-[0.9375rem] text-black text-center uppercase",
              "hover:bg-gray-50 active:bg-gray-100",
              "focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-red",
              "disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            )}
          >
            {buttonText}
          </button>
        )}
      </div>
    )
  }
)

ConsentBanner.displayName = "ConsentBanner"
